import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { configByUri } from 'src/config-by-uri';
import { MsalService } from '@azure/msal-angular';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  // Admins can test out user permissions, the selected user id is sent as header
  testPermissionsUserId?: string;

  // API URL
  apiUrl: string;

  // Current XSRF value, will be set from UserService /api/me request
  xsrf: string;

  constructor(
    public http: HttpClient,
    public snackBar: MatSnackBar,
    public router: Router,
    public dialog: MatDialog,
    public translocoService: TranslocoService,
    private msalService: MsalService,
  ) {
    this.apiUrl = configByUri.apiUri;
  }

  parseOptions(options: any = {}, xsrf = false) {
    options.params = options.params || {};

    // Clean null params from request, that are passed from filter component form group automatically
    for (const param in options.params) {
      if (options.params[param] === null || options.params[param] === undefined) {
        delete options.params[param];
      }
    }

    // Initialize headers object
    options.headers = options.headers || {};

    // Admin "login as user" functionality
    if (this.testPermissionsUserId) {
      options.headers['x-test-permissions'] = this.testPermissionsUserId;
    }

    // Fetch active language from language service to header
    options.headers['x-flow-language'] = this.translocoService.getActiveLang();

    // Observe response for reading XSRF header
    options.observe = 'response';

    // send request with credentials
    options.withCredentials = true;

    // include xsrf cookie if it is set
    if (this.xsrf && xsrf) {
      options.headers['X-XSRF-TOKEN'] = this.xsrf;
    }
  }

  async acquireMsalToken() {
    try {
      const [account] = this.msalService.instance.getAllAccounts();
      const token = await this.msalService.instance.acquireTokenSilent({
        scopes: [configByUri.msalProtectedResourceUri],
        account,
      });

      return token;
    } catch (e) {
      this.snackBar.open(this.translocoService.translate('errors.acquireToken'), 'OK', {
        panelClass: 'snackbar-error',
      });
      throw e;
    }
  }

  async get<T = any>(url: string, options?: any, disableAlert?: boolean): Promise<T> {
    try {
      options = options || {};
      this.parseOptions(options);

      const result = (await this.http.get(this.apiUrl + url, options).toPromise()) as unknown as HttpResponse<any>;

      return result.body;
    } catch (error) {
      if (!disableAlert) {
        this.errorHandler(error);
      } else {
        throw error;
      }
    }
  }

  async put(url: string, body: any, options?: any, disableAlert?: boolean): Promise<any> {
    try {
      options = options || {};
      this.parseOptions(options, true);

      const result = (await this.http
        .put(this.apiUrl + url, body, options)
        .toPromise()) as unknown as HttpResponse<any>;

      return result.body;
    } catch (error) {
      if (!disableAlert) {
        this.errorHandler(error);
      } else {
        throw error;
      }
    }
  }

  async patch(url: string, body: any, options?: any, disableAlert?: boolean): Promise<any> {
    try {
      options = options || {};
      this.parseOptions(options, true);

      const result = (await this.http
        .patch(this.apiUrl + url, body, options)
        .toPromise()) as unknown as HttpResponse<any>;

      return result.body;
    } catch (error) {
      if (!disableAlert) {
        this.errorHandler(error);
      } else {
        throw error;
      }
    }
  }

  async post(url: string, body: any, options?: any, disableAlert?: boolean): Promise<any> {
    try {
      options = options || {};
      this.parseOptions(options, true);

      const result = (await this.http
        .post(this.apiUrl + url, body, options)
        .toPromise()) as unknown as HttpResponse<any>;

      return result.body;
    } catch (error) {
      if (!disableAlert) {
        this.errorHandler(error);
      } else {
        throw error;
      }
    }
  }

  /*loginRedirect() {
    window.location.replace(`/.auth/login/aad?
      post_login_redirect_url=${encodeURIComponent(window.location.pathname + window.location.search)}`);
  }*/

  toast(message: string) {
    this.snackBar.open(message, 'OK', { panelClass: 'snackbar-info', duration: 5000 });
  }

  errorHandler(error: HttpErrorResponse) {
    // console.log(error);

    if (error.status === 401) {
      this.snackBar.open(this.translocoService.translate('errors.permissionCheckFailed'), 'OK', {
        panelClass: 'snackbar-error',
        duration: 0,
      });

      throw error;
    } else if (error.status === 0) {
      this.snackBar.open(this.translocoService.translate('errors.networkProblem'), 'OK', {
        panelClass: 'snackbar-error',
      });

      throw error;
    } else if (error.status === 504) {
      this.snackBar.open(this.translocoService.translate('errors.tryAgainLater'), 'OK', {
        panelClass: 'snackbar-error',
      });

      throw error;
    } else if (error.status === 413) {
      this.snackBar.open(this.translocoService.translate('errors.formTooLarge'), 'OK', {
        panelClass: 'snackbar-error',
      });

      throw error;
    } else if (error.status === 404) {
      this.snackBar.open(this.translocoService.translate('errors.resourceNotFound'), 'OK', {
        panelClass: 'snackbar-error',
      });

      throw error;
    } else {
      const status = error?.status;
      const message = error?.error?.message || error?.message;

      if (message && status) {
        this.snackBar.open(this.translocoService.translate('errors.generic', { message }), 'OK', {
          panelClass: 'snackbar-error',
        });
      }

      throw error;
    }
  }
}
